import StarSVG from '@public/icons/star.svg';
import classNames from 'classnames';
import { AnimatePresence, Variants, m as motion } from 'framer-motion';
import Image from 'next/image';
import ArrSVG from 'public/icons/btn-arr.svg';
import React, { useEffect, useState } from 'react';
import { Place } from 'schema-dts';
import { TransformedCard } from '@/shared/api/lib/utils/transform-program';
import { useSetFavoriteWindowState } from '@/shared/atoms/favorite-window';
import { useMediaQueryDeviceStateValue } from '@/shared/atoms/media-query-device';
import { useSetOpenedPopupsState } from '@/shared/atoms/opened-popups';
import { ImageShape } from '@/shared/lib/types';
import { easeOutQuart } from '@/shared/lib/utils/easings';
import { filterProgramsPdf } from '@/shared/lib/utils/pdf/filterPrograms';
import { ConstructorBlock } from '../../constructor/Constructor/Constructor';
import ProgramDetailPopup from '../../popups/ProgramDetailPopup';
import { PROGRAM_DETAIL_POPUP } from '../../popups/ProgramDetailPopup/ProgramDetailPopup';
import SchemaPopup, { SCHEMA_POPUP_NAME } from '../../popups/SchemaPopup/SchemaPopup';
import Button from '../../shared/Button';
import FavoriteBtn from '../../shared/FavoriteBtn';
import FavoriteWindow from '../../shared/FavoriteWindow';
import LogoSlider from '../../shared/LogoSlider';
import Typografed from '../../shared/Typografed';

type Props = {
    cards: TransformedCard[];
    isButtonVisible?: boolean;
    isFetching: boolean;
    visibleCardsCount: number;
    disableDirections: boolean;
};

export interface TransformedFavoritesCards {
    id: number;
    slug: string;
    time: string;
    prefix?: string;
    name: string;
    day: string;
    published?: string;
    place: Place | undefined;
    directions: string;
    participationFormats: string;
    companies?: ImageShape[];
    description?: string;
    content?: ConstructorBlock[];
}

const initialItem: TransformedCard = {
    id: 0,
    slug: '',
    time: '',
    name: '',
    day: '',
    place: undefined,
    directions: '',
    participationFormats: '',
};

const variants: Variants = {
    hidden: {
        y: 10,
        opacity: 0,
    },
    visible: {
        y: 0,
        opacity: 1,
    },
};

function ProgramDetailList({ cards, isButtonVisible, isFetching, visibleCardsCount, disableDirections }: Props) {
    const [favorites, setFavorites] = useState<number[]>([]);
    const [showAll, setShowAll] = useState(false);
    const [activeItem, setActiveItem] = useState<TransformedCard | undefined>(undefined);

    const device = useMediaQueryDeviceStateValue();
    const isDesktop = device === 'desktop';
    const isMobile = device.includes('mobile');

    const { openPopup } = useSetOpenedPopupsState();
    const setFavoriteWindow = useSetFavoriteWindowState();
    const visibleCards = showAll ? cards : cards.slice(0, visibleCardsCount);

    useEffect(() => {
        if (!isMobile) {
            setFavoriteWindow(favorites.length > 0);
        }
    }, [favorites, setFavoriteWindow, isMobile]);

    const toggleFavorite = (id: number) => {
        if (favorites.includes(id)) {
            setFavorites(favorites.filter((favId) => favId !== id));
        } else {
            setFavorites([...favorites, id]);
        }
    };

    const [favouritesCards, setFavouritesCards] = useState<TransformedCard[]>([]);

    useEffect(() => {
        const filteredCards = filterProgramsPdf(cards, favorites);
        if (filteredCards) {
            setFavouritesCards(filteredCards);
        }
    }, [favorites, cards]);

    return (
        <AnimatePresence>
            <motion.div
                variants={variants}
                initial="hidden"
                animate={isFetching ? 'hidden' : 'visible'}
                exit="hidden"
                transition={{ duration: 0.5, ease: easeOutQuart }}
                className="program-detail-list"
            >
                {isDesktop ? (
                    <>
                        <div className="program-detail-list-header">
                            <div className="program-detail-list-header-date text-xs">Дата и время:</div>
                            <div className="program-detail-list-header-topic text-xs">Тема:</div>
                            <div className="program-detail-list-header-place text-xs">Место:</div>
                            <div className="program-detail-list-header-format text-xs">Формат участия:</div>
                        </div>
                        <div className="program-detail-list-rows">
                            {visibleCards.map((card, i) => (
                                <div
                                    key={i}
                                    role="button"
                                    tabIndex={0}
                                    className="program-detail-list-row"
                                    onClick={() => {
                                        setActiveItem(card);
                                        if (card.description) {
                                            openPopup(PROGRAM_DETAIL_POPUP);
                                        }
                                    }}
                                >
                                    <div className="program-detail-list-row-date text-s">
                                        <div className="program-detail-list-row-date-text text-s">
                                            <p>{card.day}</p>
                                            <p className="program-detail-popup-info-item-text--time">{card.time}</p>
                                        </div>
                                    </div>
                                    <div className="program-detail-list-row-topic">
                                        {card.prefix && (
                                            <div className="program-detail-list-row-topic-type text-s">
                                                <Typografed>{card.prefix}</Typografed>
                                            </div>
                                        )}
                                        <div className="program-detail-list-row-topic-title text-m">
                                            <Typografed>{card.name}</Typografed>
                                        </div>
                                    </div>
                                    {card.place && (
                                        <button
                                            className="program-detail-list-row-place text-s"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveItem(card);
                                                if (card.place?.image) {
                                                    openPopup(SCHEMA_POPUP_NAME);
                                                }
                                            }}
                                        >
                                            <Typografed>{card.place.name}</Typografed>
                                        </button>
                                    )}
                                    <div className="program-detail-list-row-format text-s">
                                        <Typografed>{card.participationFormats}</Typografed>
                                    </div>
                                    <div className="program-detail-list-row-logo">
                                        {card.companies && card.companies.length > 1 ? (
                                            <LogoSlider images={card.companies} />
                                        ) : (
                                            card.companies &&
                                            card.companies.length === 1 && (
                                                <Image
                                                    src={card.companies[0].src}
                                                    alt={card.companies[0].alt || ''}
                                                    width={200}
                                                    height={100}
                                                />
                                            )
                                        )}
                                    </div>
                                    <div className="program-detail-list-row-right">
                                        <button
                                            className={classNames('program-detail-list-row-star', {
                                                'is-favorite': favorites.includes(card.id),
                                            })}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleFavorite(card.id);
                                            }}
                                            aria-label="Добавить в избранное"
                                        >
                                            <StarSVG />
                                        </button>
                                        <div
                                            className={classNames('program-detail-list-row-button', {
                                                'is-opacity': !card.description,
                                            })}
                                        >
                                            <ArrSVG />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                ) : (
                    visibleCards.map((card) => (
                        <div
                            key={card.id}
                            className="program-detail-list-row-mobile"
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                                setActiveItem(card);
                                if (card.description) {
                                    openPopup(PROGRAM_DETAIL_POPUP);
                                }
                            }}
                        >
                            <div className="program-detail-list-row-date text-s">
                                <div className="program-detail-list-row-date-text">
                                    <p>{card?.day}</p>
                                    <p className="program-detail-popup-info-item-text--time">{card?.time}</p>
                                </div>
                            </div>
                            <div className="program-detail-list-row-center">
                                <div className="program-detail-list-row-topic">
                                    {card.prefix && (
                                        <div className="program-detail-list-row-topic-type text-s">
                                            <Typografed>{card.prefix}</Typografed>
                                        </div>
                                    )}
                                    <div className="program-detail-list-row-topic-title text-m">
                                        <Typografed>{card.name}</Typografed>
                                    </div>
                                </div>
                                <div className="program-detail-list-row-mobile-item">
                                    <div className="program-detail-list-row-topic-type text-xs">Место:</div>
                                    {card.place && (
                                        <button
                                            className="program-detail-list-row-place text-s"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setActiveItem(card);
                                                if (card.place?.image) {
                                                    openPopup(SCHEMA_POPUP_NAME);
                                                }
                                            }}
                                        >
                                            <Typografed>{card.place.name}</Typografed>
                                        </button>
                                    )}
                                </div>
                                <div className="program-detail-list-row-mobile-item">
                                    <div className="program-detail-list-row-topic-type text-xs">Формат участия:</div>
                                    <div className="program-detail-list-row-format text-s">
                                        <Typografed>{card.participationFormats}</Typografed>
                                    </div>
                                </div>
                                {card.companies && card.companies.length > 1 ? (
                                    <div className="program-detail-list-row-logo">
                                        <LogoSlider images={card.companies} />
                                    </div>
                                ) : (
                                    card.companies &&
                                    card.companies.length === 1 && (
                                        <div className="program-detail-list-row-logo">
                                            <Image
                                                src={card.companies[0].src}
                                                alt={card.companies[0].alt || ''}
                                                width={200}
                                                height={100}
                                            />
                                        </div>
                                    )
                                )}
                            </div>
                            <div className="program-detail-list-row-right">
                                <button
                                    className={classNames('program-detail-list-row-star', {
                                        'is-favorite': favorites.includes(card.id),
                                    })}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        toggleFavorite(card.id);
                                    }}
                                >
                                    <StarSVG />
                                </button>
                                <div
                                    className={classNames('program-detail-list-row-button', {
                                        'is-opacity': !card.description,
                                    })}
                                >
                                    <ArrSVG />
                                </div>
                            </div>
                        </div>
                    ))
                )}

                {!showAll && cards.length > visibleCardsCount && (
                    <div className="program-detail-list-btn-wrapper">
                        <Button
                            className="program-detail-list-btn"
                            variant="outline-dark"
                            onClick={() => setShowAll(true)}
                        >
                            Показать все
                        </Button>
                    </div>
                )}
                <FavoriteWindow favorites={favouritesCards} setFavorites={setFavorites} />
                <AnimatePresence>
                    {favouritesCards.length > 0 && isMobile && isButtonVisible && (
                        <FavoriteBtn isScrollButtonVisible={isButtonVisible} />
                    )}
                </AnimatePresence>

                <ProgramDetailPopup program={activeItem || initialItem} disableDirections={disableDirections} />
                {activeItem && activeItem.place && (
                    <SchemaPopup image={activeItem.place.image} content={{ name: activeItem.place.name }} isBackBtn />
                )}
            </motion.div>
        </AnimatePresence>
    );
}

export default ProgramDetailList;
